/**
 * AI:
 * Status: "confirmed"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: The ONE reusable SEPA mandate block of cr.CustomerBankAccount (projects/sepa_storage
 *   R-32). Both bank account OBCs (create and change) embed this component instead of carrying
 *   mandate logic of their own — duplicated mandate logic is an acceptance defect. It owns the
 *   mandate source selection, the three capture branches of R-33 and the mandate status display,
 *   and it deliberately owns NO account data (IBAN/BIC/holder) — those belong to the OBCs.
 */
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {catchError, forkJoin, map, Observable, of, Subscription} from "rxjs";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../../core/shared/service/crud/mvs-crud.service";
import {MvsCrudModeEnum} from "../../../../../core/shared/service/crud/mvs-crud-mode.enum";
import {MvsUiObjectService} from "../../../../../core/shared/object/service/mvs-ui-object.service";
import {ObjectRequestList} from "../../../../../core/shared/dto/object/request/object-request-list";
import {Sorting} from "../../../../../core/shared/misc/sorting";
import {DtoList} from "../../../../../core/shared/dto/dto.list";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";
import {MvsFormControlOverwrite} from "../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite";
import {MvsFormFieldAccessEnum} from "../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum";
import {
    MvsFormStateDto
} from "../../../../../core/shared/crud/components/mvs-crud-object-form/dto/mvs-form-state.dto";
import {DtoDetail} from "../../../../../core/shared/dto/dto.detail";
import {DatesHelper} from "../../../../../core/shared/helper/dates-helper";
import {DmDocumentDto} from "../../../../../feature-core/dm/model/dto/entity/dm-document.dto";
import {CustomerBankAccountDto} from "../../../model/dto/entity/customer-bank-account.dto";
import {CustomerSepaMandateSourceDto} from "../../../model/dto/entity/customer-sepa-mandate-source.dto";
import {CustomerSepaMandateSourceInternalTypeEnum} from "../../../../../core/shared/enum/customer-sepa-mandate-source-internal-type.enum";
import {CustomerSepaMandateStatusEnum} from "../../../enum/customer-sepa-mandate-status.enum";
import {CrSepaMandateCaptureRequestDto} from "../../../model/dto/api/cr-sepa-mandate-capture-request.dto";
import {CrSepaMandateStateDto} from "../../../model/dto/api/cr-sepa-mandate-state.dto";
import {CrSepaMandateSignerRoleDto} from "../../../model/dto/api/cr-sepa-mandate-signer-role.dto";
import {CustomerBankAccountService} from "../../../service/api/customer-bank-account.service";
import {CrSepaMandateSignerStatusEnum} from "../../../model/enum/cr-sepa-mandate-signer-status.enum";
import {
    CrSepaMandateCaptureMode,
    CrSepaMandateCapturedEvent
} from "../../../logic/cr-sepa-mandate-capture-contract";
import {
    CR_SEPA_MANDATE_CAPTURE_FIELDS,
    excludeNonCaptureBankAccountFields
} from "../../../logic/cr-bank-account-mandate-fields";

/**
 * One selectable {@code cr.CustomerSepaMandateSource}, prepared for the source picker so the
 * template renders the capture type as a badge without reaching back into the DTO list.
 */
interface CrSepaMandateSourceOption {

    value: number;

    label: string;

    internalType: CustomerSepaMandateSourceInternalTypeEnum;
}

/**
 * Severity accepted by {@code p-tag}. Declared as the literal union rather than {@code string} so
 * the template type check catches a typo instead of PrimeNG silently dropping the styling.
 */
type CrSepaMandateBadgeSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast';

/**
 * Visual presentation of one enum value — the single place that maps an ordinal to its German
 * label, its Font Awesome icon and its PrimeNG severity.
 */
interface CrSepaMandateBadge {

    label: string;

    icon: string;

    severity: CrSepaMandateBadgeSeverity;
}

/**
 * Neutral note about a mandate that has been started but is not valid yet, or has failed.
 *
 * Exists because {@link CrSepaMandateCaptureComponent.mandateCaptured} no longer fires for those
 * states: without it the user would run an action, see the status badge move and get no sentence
 * telling them what is now expected of them.
 */
interface CrSepaMandateProgressMessage {

    severity: 'info' | 'warn' | 'error';

    text: string;
}

@Component({
    selector: 'cr-sepa-mandate-capture',
    templateUrl: './cr-sepa-mandate-capture.component.html',
    styleUrls: ['./cr-sepa-mandate-capture.component.scss'],
    standalone: false
})
export class CrSepaMandateCaptureComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * The {@code cr.Customer} the bank account belongs to. Mandatory.
     */
    @Input() customerId: number;

    /**
     * The {@code cr.CustomerBankAccount} the mandate is captured for. Null in the create context
     * until the account has been saved — the mandate needs a persisted account as its anchor.
     */
    @Input() bankAccountId: number | null;

    /**
     * Capture context, see {@link CrSepaMandateCaptureMode}.
     */
    @Input() mode: CrSepaMandateCaptureMode = 'create';

    /**
     * Display only — no source selection, no capture, no revocation.
     *
     * NAME-011: a boolean input carries an {@code is} prefix. The bare name would additionally
     * shadow the TypeScript {@code readonly} modifier keyword on the class member.
     */
    @Input() isReadonly: boolean = false;

    /**
     * Whether this block should OFFER the mandate source picker, or present the source it already
     * found on the account as a read-only line with an "Ändern" link (projects/sepa_storage,
     * DESIGN_GATE 3.1).
     *
     * <h3>What it does NOT do</h3>
     * It does not preselect, preload or otherwise inject a source. There is exactly one source of
     * truth for the selection and it stays {@code account.sepaMandateSourceDtoId}, read in
     * {@link refreshComponent} ("the persisted source wins"). This input only decides how the SAME
     * resolved source is PRESENTED — picker or read-only line — so no second truth can arise next to
     * the account (3.2 rejects a preselection input for exactly that reason).
     *
     * <h3>Default true keeps every existing caller identical</h3>
     * The change OBC and the create OBC's "no source was chosen in Abschnitt 1" case leave it alone
     * and get today's behaviour unchanged, badges and all three diagnoses included. Only the guided
     * create OBC passes {@code false}, and only after its create POST RESPONSE reported a stored
     * source — the user has just answered that question one screen up, and asking again with an empty
     * picker next to it is what this exists to avoid.
     *
     * <h3>False never removes the way back</h3>
     * The "Ändern" link reveals the very same picker for the current mounting
     * ({@link handleRevealSourcePicker}). Correcting the source stays possible at all times (rule 5),
     * it just is not the default question anymore.
     */
    @Input() isSourceSelectionOffered: boolean = true;

    /**
     * Emitted once a mandate has been captured, with the status it reached.
     */
    @Output() mandateCaptured: EventEmitter<CrSepaMandateCapturedEvent> =
        new EventEmitter<CrSepaMandateCapturedEvent>();

    /**
     * Emitted on every mandate state the component learns about — initial read included — so the
     * hosting OBC can mirror the status without a read of its own.
     */
    @Output() mandateStateChanged: EventEmitter<CrSepaMandateStateDto> =
        new EventEmitter<CrSepaMandateStateDto>();

    initialized: boolean = false;
    busy: boolean = false;

    /**
     * The selectable mandate sources — all of them, see {@link loadSources} on why there is no
     * active-only narrowing.
     */
    sources: CustomerSepaMandateSourceDto[] = [];
    sourceOptions: CrSepaMandateSourceOption[] = [];

    selectedSourceId: number | null = null;
    selectedSource: CustomerSepaMandateSourceDto = null;

    /**
     * The user asked for the picker through "Ändern" although {@link isSourceSelectionOffered} is
     * false.
     *
     * Scoped to the CURRENT MOUNTING and reset in {@link refreshComponent} together with everything
     * else derived from the account: that method runs when the account underneath changes and after
     * "Erneut laden", and in both cases the source shown afterwards is freshly read from the row, i.e.
     * exactly the state the read-only line is meant to present. The link is one click away again.
     */
    isSourcePickerRevealed: boolean = false;

    /**
     * Current mandate state, null while none could be read (no account yet, or read failed).
     */
    state: CrSepaMandateStateDto = null;

    /**
     * Failure of an action or a read triggered by this component — distinct from
     * {@link CrSepaMandateStateDto.errorFlag}, which reports a failure of the backend mandate run.
     */
    actionErrorMessage: string = null;

    /**
     * The mandate sources could not be loaded. Kept apart from "none configured": an empty picker
     * that says nothing is the worst failure mode a capture screen has, because the user cannot
     * tell a missing configuration from a broken read.
     */
    sourcesErrorMessage: string = null;

    /**
     * The mandate state could not be read, so the status shown may be stale.
     */
    stateErrorMessage: string = null;

    /**
     * The bank account itself could not be read, so the mandate source persisted on it is unknown.
     *
     * Reported rather than swallowed, and this one is not cosmetic. The source picker comes up EMPTY
     * after a failed account read even though the account may already carry a source; picking one
     * then PUTs a source onto an account that already had a different one, silently changing how the
     * mandate is produced. The user has to be told that the pre-selection is missing, not left to
     * infer it from a blank field.
     */
    accountErrorMessage: string = null;

    /**
     * Signature date of the {@code withDocument} branch. Request parameter of
     * {@code attachMandateDocument}, not a form field of the account — the endpoint performs the
     * write, so no CRUD form is involved here.
     */
    documentSignatureDate: Date = null;

    /**
     * Anchor of the upload in the {@code withDocument} branch.
     */
    bankAccountIdentifier: ObjectIdentifier = null;

    /**
     * Field scope of the {@code withoutDocument} capture form: signature date and reason only.
     */
    formControlOverwrite: MvsFormControlOverwrite;

    /**
     * Field scope of the {@code withoutDocumentNoReason} capture form: signature date only — the
     * missing-document reason is EXCLUDED, it is neither shown nor required for that capture type.
     */
    formControlOverwriteNoReason: MvsFormControlOverwrite;

    /**
     * Latest state of the embedded capture form — this component owns its save button, so it needs
     * to know when the form is submittable.
     */
    formState: MvsFormStateDto = null;

    /**
     * Latest values of the embedded capture form — the gate for the caller owned save button (the
     * reason is mandatory, D-24) AND the payload of {@link handleCaptureWithoutDocument}. The form
     * itself never writes.
     *
     * Null until the user types: {@code MvsCrudObjectFormComponent} deliberately emits no baseline
     * event on the first build. Both capture attributes are user input in this branch, so there is
     * nothing to read before the first keystroke anyway.
     */
    formValues: CustomerBankAccountDto = null;

    protected readonly CustomerSepaMandateSourceInternalTypeEnum = CustomerSepaMandateSourceInternalTypeEnum;
    protected readonly CustomerSepaMandateStatusEnum = CustomerSepaMandateStatusEnum;

    private sepaMandateSourceService: MvsCrudService;
    private subscription: Subscription = new Subscription();

    constructor(
        protected coreService: MvsCoreService,
        protected objectService: MvsUiObjectService,
        protected bankAccountService: CustomerBankAccountService,
        protected confirmationService: ConfirmationService) {

        this.sepaMandateSourceService = this.coreService.getCrudService('cr.CustomerSepaMandateSource');
    }

    ngOnInit(): void {
        this.initComponent();
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        if (changes['bankAccountId'] || changes['customerId'] || changes['mode']) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    protected initComponent(): void {

        this.setupFormControlOverwrite();
        this.refreshComponent();
        this.initialized = true;
    }

    /**
     * Reload everything the component shows: the selectable sources, the persisted source of the
     * account and the current mandate state.
     *
     * EVERYTHING DERIVED FROM THE PREVIOUS ACCOUNT IS DROPPED FIRST. This method also runs on an
     * {@code ngOnChanges} of {@link bankAccountId} — the change OBC swaps the account underneath a
     * mounted component. Without the reset the picker kept the previous account's source, so the
     * capture branch on screen was the previous account's branch, and the half-typed capture values
     * of the previous account were still standing when the new one loaded.
     */
    protected refreshComponent(): void {

        this.actionErrorMessage = null;
        this.accountErrorMessage = null;

        this.selectedSourceId = null;
        this.selectedSource = null;
        this.isSourcePickerRevealed = false;
        this.formValues = null;
        this.formState = null;
        this.documentSignatureDate = null;

        this.bankAccountIdentifier = this.bankAccountId
            ? new ObjectIdentifier('cr.CustomerBankAccount', this.bankAccountId)
            : null;

        this.busy = true;

        this.subscription.add(
            forkJoin({
                sources: this.loadSources(),
                account: this.loadAccount(),
                state: this.loadState()
            }).subscribe({
                next: (result) => {

                    this.applySources(result.sources);

                    // The persisted source wins over a local pre-selection: it is what the backend
                    // actions read to decide how the mandate is produced.
                    if (result.account?.sepaMandateSourceDtoId) {
                        this.selectedSourceId = result.account.sepaMandateSourceDtoId;
                    }
                    this.resolveSelectedSource();

                    this.applyState(result.state, false);
                    this.busy = false;
                },
                error: (error) => {
                    this.reportActionError('Der Mandatsstand konnte nicht geladen werden.', error);
                    this.busy = false;
                }
            })
        );
    }

    /**
     * Field scope of the {@code withoutDocument} capture form: the two capture attributes are
     * visible, everything else on the entity is EXCLUDED.
     *
     * The whitelist of {@code createHideAll} alone is NOT enough. Its default for a field left out
     * is HIDDEN, and a hidden field keeps its form control and submits the snapshot the form
     * loaded — so this form would push the account data (IBAN included) and a possibly stale
     * {@code sepaMandateSourceDtoId} back on every mandate save. The account form of the change OBC
     * stands open on the SAME entity row, and the source picker writes the source with a PUT while
     * this form is still loading. {@link excludeNonCaptureBankAccountFields} takes both groups out
     * of the form group entirely; the reasoning is written out there.
     */
    private setupFormControlOverwrite(): void {

        this.formControlOverwrite = MvsFormControlOverwrite.createHideAll(
            ...CR_SEPA_MANDATE_CAPTURE_FIELDS);

        excludeNonCaptureBankAccountFields(this.formControlOverwrite);

        this.formControlOverwrite.addFieldInputControl('sepaMandateMissingDocumentReason', 'textarea');
        this.formControlOverwrite.addFieldLabel('sepaDateOfSignature', 'Unterschriftsdatum');
        this.formControlOverwrite.addFieldLabel(
            'sepaMandateMissingDocumentReason', 'Begründung für das fehlende Beleg-Dokument');

        // withoutDocumentNoReason: same scope, but the reason is taken out of the form group entirely
        this.formControlOverwriteNoReason = MvsFormControlOverwrite.createHideAll('sepaDateOfSignature');
        excludeNonCaptureBankAccountFields(this.formControlOverwriteNoReason);
        this.formControlOverwriteNoReason.addField(
            'sepaMandateMissingDocumentReason', MvsFormFieldAccessEnum.EXCLUDED);
        this.formControlOverwriteNoReason.addFieldLabel('sepaDateOfSignature', 'Unterschriftsdatum');
    }

    /**
     * Load the selectable mandate sources.
     *
     * NO status filter, and that is a finding rather than a simplification: R-33 asks for "only
     * active sources", but {@code cr.CustomerSepaMandateSource} has nothing to filter on. It extends
     * {@code AuditableEntity}, not {@code ConfigurableEntity}, so it never got the
     * {@code entityStatus} of the configuration base class — the generated schema
     * ({@code db/postgre/V1__.sql}) gives {@code cr_customer_sepa_mandate_source} exactly
     * {@code id, name, requires_document, internal_type}, the five config FKs,
     * {@code signer_role_key}, {@code number_range_id}, {@code mandate_identifier_template} and the
     * audit columns. There is no validity period either. A filter on {@code entityStatus} would
     * either throw or be ignored, and both outcomes are worse than none. Same read, same conclusion
     * in {@code bm-cbacw-step-payment-method.component.ts} (bm-customer-billing-account-change-wizard,
     * successor of the former bm-wizard-step-payment-method), which lists this entity unfiltered.
     * Making "only active" real needs a backend change; see the report on this component.
     *
     * Returns null — NOT an empty list — when the read failed, so the caller can tell "nothing
     * configured" from "could not load" and say which one it is.
     */
    private loadSources(): Observable<CustomerSepaMandateSourceDto[] | null> {

        const request = ObjectRequestList.createBasic(false, [], [new Sorting('name', true)]);

        return this.sepaMandateSourceService.list(request).pipe(
            map((dtoList: DtoList) => (dtoList?.entries ?? []) as CustomerSepaMandateSourceDto[]),
            catchError((error) => {
                console.error('cr-sepa-mandate-capture: mandate sources could not be loaded', error);
                return of(null as CustomerSepaMandateSourceDto[] | null);
            })
        );
    }

    /**
     * Read the account for the persisted mandate source. Independent of the state read on purpose:
     * a source already chosen must stay visible even when the state endpoint is unavailable.
     *
     * Reports a failed read, like {@link loadSources} and {@link loadState} do. Swallowing it was a
     * real defect and not merely untidy: the persisted {@code sepaMandateSourceDtoId} is lost, the
     * picker comes up empty, and a fresh pick then PUTs a source onto an account that already had
     * one — changing how the mandate is produced without anybody being told.
     */
    private loadAccount(): Observable<CustomerBankAccountDto> {

        this.accountErrorMessage = null;

        if (!this.bankAccountId) {
            return of(null);
        }

        return this.bankAccountService.get(this.bankAccountId).pipe(
            map((dto: DtoDetail) => dto as CustomerBankAccountDto),
            catchError((error) => {
                console.error('cr-sepa-mandate-capture: bank account could not be read', error);
                this.accountErrorMessage =
                    'Die Bankverbindung konnte nicht gelesen werden. Eine bereits hinterlegte '
                    + 'Mandatsquelle wird deshalb nicht vorausgewählt — bitte prüfen Sie die Auswahl, '
                    + 'bevor Sie ein Mandat erfassen.';
                return of(null as CustomerBankAccountDto);
            })
        );
    }

    /**
     * Read the mandate state. Reports a failed read instead of swallowing it: without the state the
     * component cannot tell "no mandate" from "status unknown", and showing the {@code none} badge
     * for an account that carries an active mandate is the same silent-empty failure mode as an
     * empty source picker.
     */
    private loadState(): Observable<CrSepaMandateStateDto> {

        this.stateErrorMessage = null;

        if (!this.bankAccountId) {
            return of(null);
        }

        return this.bankAccountService.getMandateState(this.bankAccountId).pipe(
            catchError((error) => {
                console.error('cr-sepa-mandate-capture: mandate state could not be read', error);
                this.stateErrorMessage =
                    'Der Mandatsstand konnte nicht gelesen werden. Die angezeigte Statusangabe ist '
                    + 'deshalb möglicherweise nicht aktuell.';
                return of(null as CrSepaMandateStateDto);
            })
        );
    }

    /**
     * Adopt the loaded sources. A null list means the read failed — that is reported, never shown as
     * an empty picker.
     */
    private applySources(sources: CustomerSepaMandateSourceDto[] | null): void {

        if (!sources) {
            this.sources = [];
            this.sourceOptions = [];
            this.sourcesErrorMessage =
                'Die Mandatsquellen konnten nicht geladen werden. Ohne sie kann kein Mandat erfasst '
                + 'werden.';
            return;
        }

        this.sourcesErrorMessage = null;
        this.sources = sources;
        this.sourceOptions = this.sources.map(source => ({
            value: source.id,
            label: source.name,
            internalType: source.internalType
        }));
    }

    private resolveSelectedSource(): void {
        this.selectedSource = this.sources.find(source => source.id === this.selectedSourceId) ?? null;
    }

    /**
     * Adopt a mandate state and publish it. {@code captured} marks the states that came out of a
     * capture action rather than out of a plain read.
     *
     * {@link mandateCaptured} fires for {@code active} ONLY, even though every action result passes
     * through here. The four endpoints report a failed run as a STATE, not as an exception (the
     * controller does this deliberately so the reason survives), and both OBCs announce
     * "Das Mandat wurde für die Bankverbindung erfasst." unconditionally on this event. Firing it
     * for {@code failed} would put a green success message next to the red error box — exactly the
     * diagnosability the error fields of D-22 were introduced to protect. {@code documentPending}
     * and {@code signaturePending} are not failures but they are not a captured mandate either;
     * they reach the host through {@link mandateStateChanged} and the user through
     * {@link getProgressMessage}.
     */
    private applyState(state: CrSepaMandateStateDto, captured: boolean): void {

        this.state = state;

        if (!state) {
            return;
        }

        this.mandateStateChanged.emit(state);

        if (captured && state.sepaMandateStatus === CustomerSepaMandateStatusEnum.active) {
            this.mandateCaptured.emit({
                bankAccountId: state.bankAccountId,
                status: state.sepaMandateStatus
            });
        }
    }

    private reportActionError(message: string, error?: any): void {

        this.actionErrorMessage = message;
        console.error('cr-sepa-mandate-capture:', message, error);
    }

    /**
     * Re-read the mandate state after a write whose response is not a mandate state — today that is
     * the CRUD update of the mandate source in {@link handleSourceChange}. The four action endpoints
     * all answer WITH the state and are adopted directly, never through here.
     */
    private reloadState(captured: boolean): void {

        if (!this.bankAccountId) {
            return;
        }

        this.busy = true;

        this.subscription.add(
            this.bankAccountService.getMandateState(this.bankAccountId).subscribe({
                next: (state: CrSepaMandateStateDto) => {
                    this.applyState(state, captured);
                    this.busy = false;
                },
                error: (error) => {
                    this.reportActionError('Der Mandatsstand konnte nicht neu geladen werden.', error);
                    this.busy = false;
                }
            })
        );
    }

    // ---------------------------------------------------------------------------------------
    // Source selection
    // ---------------------------------------------------------------------------------------

    /**
     * Persist the chosen mandate source on the account.
     *
     * The three capture branches all need it stored: {@code attachMandateDocument} and
     * {@code startMandateSignature} carry no source of their own, the backend reads it off the
     * account to decide how the mandate is produced.
     */
    /**
     * Retry after a failed read — sources, account and state together, since all three come out of
     * the one {@link refreshComponent} join. A reported failure the user cannot act on is only half
     * an improvement over a silent one.
     */
    handleReloadSources(): void {
        this.refreshComponent();
    }

    /**
     * Is the source PICKER on screen, as opposed to the read-only source line?
     *
     * The picker markup, its two diagnoses, the capture-type badges and {@link handleSourceChange}
     * exist exactly once either way — this only decides whether that one block is rendered. A caller
     * that suppressed it can always get it back through "Ändern".
     */
    isSourcePickerVisible(): boolean {
        return this.isSourceSelectionOffered || this.isSourcePickerRevealed;
    }

    /**
     * What the read-only source line says when the picker is suppressed.
     *
     * Three cases and they must not be collapsed. "Keine Mandatsquelle hinterlegt" is a statement
     * about the ACCOUNT, so it may only be made when the account really carries no source. When the
     * account carries one that {@link resolveSelectedSource} could not match — the list came back
     * without it, e.g. because the source was deleted — saying "none" would be a lie about persisted
     * data, and the user would have no way to see that something IS set.
     */
    getSelectedSourceDisplayLabel(): string {

        if (this.selectedSource) {
            return this.selectedSource.name;
        }

        if (this.selectedSourceId) {
            return 'Die hinterlegte Mandatsquelle ist nicht mehr auswählbar.';
        }

        return 'Keine Mandatsquelle hinterlegt';
    }

    /**
     * "Ändern" — show the picker after all, for this mounting.
     *
     * Nothing is written and nothing is cleared here: the account keeps the source it has until the
     * user actually picks a different one, at which point {@link handleSourceChange} runs exactly as
     * it does for every other caller.
     */
    handleRevealSourcePicker(): void {
        this.isSourcePickerRevealed = true;
    }

    handleSourceChange(): void {

        this.actionErrorMessage = null;
        this.resolveSelectedSource();
        this.formValues = null;
        this.formState = null;
        this.documentSignatureDate = null;

        if (!this.bankAccountId || !this.selectedSourceId) {
            return;
        }

        const updateDto = new CustomerBankAccountDto();
        updateDto.id = this.bankAccountId;
        updateDto.customerDtoId = this.customerId;
        updateDto.sepaMandateSourceDtoId = this.selectedSourceId;

        this.busy = true;

        this.subscription.add(
            this.bankAccountService.update(updateDto).subscribe({
                next: () => {
                    this.busy = false;
                    this.reloadState(false);
                },
                error: (error) => {
                    this.reportActionError('Die Mandatsquelle konnte nicht gespeichert werden.', error);
                    this.busy = false;
                }
            })
        );
    }

    // ---------------------------------------------------------------------------------------
    // Branch: withoutDocument
    // ---------------------------------------------------------------------------------------

    handleFormStateChange(formState: MvsFormStateDto): void {
        this.formState = formState;
    }

    /**
     * The values the capture endpoint is called with.
     *
     * The embedded {@code mvs-crud-object} is an INPUT SURFACE here and nothing else — it renders
     * the two capture attributes from the entity's own form metadata (label, type, mandatory flag)
     * and never writes. The write is {@link handleCaptureWithoutDocument}. That is why the form
     * carries neither {@code onChangedObject} nor {@code onObjectChangeError}: it has nothing to
     * report on either channel.
     */
    handleFormChange(dto: DtoDetail): void {
        this.formValues = dto as CustomerBankAccountDto;
    }

    /**
     * The capture-without-document branch serves both {@code withoutDocument} and
     * {@code withoutDocumentNoReason} — same action, same activation; they differ only in the reason.
     */
    isWithoutDocumentBranch(): boolean {

        const internalType = this.getBranchInternalType();
        return internalType === CustomerSepaMandateSourceInternalTypeEnum.withoutDocument
            || internalType === CustomerSepaMandateSourceInternalTypeEnum.withoutDocumentNoReason;
    }

    /**
     * The missing-document reason is shown and required for {@code withoutDocument} only —
     * {@code withoutDocumentNoReason} captures without it by definition.
     */
    isMissingDocumentReasonRequired(): boolean {
        return this.getBranchInternalType() === CustomerSepaMandateSourceInternalTypeEnum.withoutDocument;
    }

    /**
     * The signature date is always mandatory. For {@code withoutDocument} the reason is mandatory as
     * well: it is the deliberate substitute for a dedicated permission (D-24), so there is no capture
     * without a document AND without a justification. {@code withoutDocumentNoReason} needs the date
     * only.
     */
    isWithoutDocumentSubmittable(): boolean {

        if (this.formState?.submittable !== true) {
            return false;
        }

        if (!this.formValues?.sepaDateOfSignature) {
            return false;
        }

        if (!this.isMissingDocumentReasonRequired()) {
            return true;
        }

        const reason = this.formValues?.sepaMandateMissingDocumentReason;
        return !!reason && reason.trim().length > 0;
    }

    /**
     * Capture the mandate and activate it — the {@code withoutDocument} branch of R-33.
     *
     * THIS IS AN ACTION, NOT A FORM SAVE, and the difference is the whole point of the endpoint.
     * The branch used to call {@code withoutDocumentForm.save()}, an ordinary CRUD write of the two
     * capture attributes. {@code CustomerBankAccountOnChange} DERIVES a mandate status where none is
     * given but never RAISES one, so that write left the account on {@code none}: no mandate
     * reference, invisible to {@code hasActiveSepaMandate} and to the pain.008, and
     * {@link mandateCaptured} never fired. Sending the status along instead is forbidden by R-28.
     *
     * The chosen source travels WITH the capture, so the pick and the capture are one write. The
     * response is the new state and is adopted directly — asking for the state again afterwards
     * would race the write and could answer from before it.
     */
    handleCaptureWithoutDocument(): void {

        if (!this.bankAccountId || !this.isWithoutDocumentSubmittable()) {
            return;
        }

        // withoutDocumentNoReason: the reason is not part of the capture - not sent at all
        const request: CrSepaMandateCaptureRequestDto = {
            sepaDateOfSignature: this.toRequestDate(this.formValues.sepaDateOfSignature),
            sepaMandateMissingDocumentReason: this.isMissingDocumentReasonRequired()
                ? this.formValues.sepaMandateMissingDocumentReason?.trim()
                : undefined,
            sepaMandateSourceDtoId: this.selectedSourceId ?? undefined
        };

        this.actionErrorMessage = null;
        this.busy = true;

        this.subscription.add(
            this.bankAccountService.captureMandate(this.bankAccountId, request).subscribe({
                next: (state: CrSepaMandateStateDto) => {
                    this.applyState(state, true);
                    this.busy = false;
                },
                error: (error) => {
                    this.reportActionError('Das Mandat konnte nicht erfasst werden.', error);
                    this.busy = false;
                }
            })
        );
    }

    /**
     * Serialize a captured day into the {@code 'yyyy-MM-dd'} a backend {@code LocalDate} expects.
     *
     * THE SYMPTOM THIS EXISTS FOR — "the mandate is signed one day before the date on screen":
     * handing a raw {@code Date} to a request body lets {@code JSON.stringify} serialize it through
     * {@code toISOString()}, so a capture on an afternoon east of Greenwich travels as the previous
     * day and is stored as such.
     *
     * TWO OPPOSITE {@code Date} CONVENTIONS reach this method and it must not pick one reader:
     * the framework date picker inside {@code mvs-crud-object} hands out UTC midnight of the picked
     * day ({@code MvsFormFieldDatePickerComponent#convertToUTCWithoutChangingDate}), while a bare
     * {@code p-datePicker} — the {@code withDocument} branch — hands out LOCAL midnight. The
     * discriminator is the UTC time-of-day: at any non-zero offset a local-midnight value has a
     * NON-zero UTC time-of-day, so only a picker value can be exactly midnight in UTC. Same reading,
     * same reasoning as {@code BmCustomerBillingAccountChangeWizardStateService#toRequestDate} (taken
     * over from the former BmPaymentChangeWizardStateService).
     */
    private toRequestDate(value: Date | string | null): string {

        if (!value) {
            return null;
        }

        // already a day string — taken VERBATIM; re-parsing it is the round trip that shifts a day
        if (typeof value === 'string') {
            const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
            return match ? match[1] : null;
        }

        if (isNaN(value.getTime())) {
            return null;
        }

        const isUtcCalendarDay = value.getUTCHours() === 0
            && value.getUTCMinutes() === 0
            && value.getUTCSeconds() === 0
            && value.getUTCMilliseconds() === 0;

        return isUtcCalendarDay ? DatesHelper.formatUtcToYMD(value) : DatesHelper.formatToYMD(value);
    }

    // ---------------------------------------------------------------------------------------
    // Branch: withDocument
    // ---------------------------------------------------------------------------------------

    isDocumentUploadEnabled(): boolean {
        return !this.isReadonly && !!this.bankAccountId && !!this.documentSignatureDate;
    }

    /**
     * Book the uploaded document onto the mandate.
     */
    handleDocumentUploaded(document: DmDocumentDto): void {

        if (!document?.id || !this.bankAccountId || !this.documentSignatureDate) {
            return;
        }

        this.actionErrorMessage = null;
        this.busy = true;

        this.subscription.add(
            this.bankAccountService.attachMandateDocument(this.bankAccountId, {
                sepaMandateDocumentDtoId: document.id,
                sepaDateOfSignature: this.toRequestDate(this.documentSignatureDate)
            }).subscribe({
                next: (state: CrSepaMandateStateDto) => {
                    this.applyState(state, true);
                    this.busy = false;
                },
                error: (error) => {
                    this.reportActionError('Das hochgeladene Dokument konnte nicht zugeordnet werden.', error);
                    this.busy = false;
                }
            })
        );
    }

    // ---------------------------------------------------------------------------------------
    // Branch: withSmProcess
    // ---------------------------------------------------------------------------------------

    isSignatureStartAllowed(): boolean {

        if (this.isReadonly || this.busy || !this.bankAccountId) {
            return false;
        }

        return this.state?.sepaMandateStatus !== CustomerSepaMandateStatusEnum.signaturePending;
    }

    handleStartSignature(): void {

        if (!this.bankAccountId) {
            return;
        }

        this.actionErrorMessage = null;
        this.busy = true;

        this.subscription.add(
            this.bankAccountService.startMandateSignature(this.bankAccountId).subscribe({
                next: (state: CrSepaMandateStateDto) => {
                    this.applyState(state, true);
                    this.busy = false;
                },
                error: (error) => {
                    this.reportActionError(
                        'Das Mandat konnte nicht erzeugt und zur Unterschrift gegeben werden.', error);
                    this.busy = false;
                }
            })
        );
    }

    // ---------------------------------------------------------------------------------------
    // Revocation
    // ---------------------------------------------------------------------------------------

    /**
     * Revocation is offered on an existing account only, and only for an active mandate.
     */
    isRevocationAllowed(): boolean {

        return !this.isReadonly
            && this.mode === 'change'
            && !!this.bankAccountId
            && this.state?.sepaMandateStatus === CustomerSepaMandateStatusEnum.active;
    }

    /**
     * Revoke the mandate — behind a confirmation, because this is the one destructive action of the
     * component.
     *
     * A revocation ends the direct debit collection, cannot be undone from the UI, and the button
     * sits in the same panel and at the same click-target size as the harmless controls. Neither
     * {@link isRevocationAllowed} (an eligibility gate) nor {@link busy} (a re-entrancy flag) is a
     * confirmation, so a single misplaced click used to be enough.
     *
     * The eligibility gate stays OUTSIDE the confirmation: an ineligible state must short-circuit
     * without asking anything. The confirmation is keyless on purpose — it is answered by the single
     * global {@code p-confirmDialog} of {@code app.main.component.html}. A local one in this
     * template would produce a second, duplicated dialog.
     */
    handleRevoke(): void {

        if (!this.isRevocationAllowed()) {
            return;
        }

        this.confirmationService.confirm({
            header: 'SEPA-Mandat widerrufen',
            message: this.getRevocationConfirmationMessage(),
            icon: 'fa-regular fa-triangle-exclamation',
            acceptLabel: 'Mandat widerrufen',
            rejectLabel: 'Abbrechen',
            accept: () => {

                this.actionErrorMessage = null;
                this.busy = true;

                this.subscription.add(
                    this.bankAccountService.revokeMandate(this.bankAccountId).subscribe({
                        next: (state: CrSepaMandateStateDto) => {
                            this.applyState(state, false);
                            this.busy = false;
                        },
                        error: (error) => {
                            this.reportActionError('Das Mandat konnte nicht widerrufen werden.', error);
                            this.busy = false;
                        }
                    })
                );
            }
        });
    }

    /**
     * The question the revocation confirmation asks. It NAMES the mandate, so the user can tell
     * they picked the right record before confirming.
     *
     * The mandate reference is used when one was assigned — it is the identifying attribute the
     * summary above the button already shows. It is optional on {@link CrSepaMandateStateDto}
     * (R-30 assigns it during capture), hence the neutral fallback. The component deliberately owns
     * no account data, so there is no IBAN available here to name instead.
     */
    private getRevocationConfirmationMessage(): string {

        const subject = this.state?.sepaMandateIdentifier
            ? `das SEPA-Mandat mit der Mandatsreferenz ${this.state.sepaMandateIdentifier}`
            : 'das SEPA-Mandat dieser Bankverbindung';

        return `Möchten Sie ${subject} wirklich widerrufen? Der Widerruf beendet den `
            + 'Lastschrifteinzug und kann nicht rückgängig gemacht werden.';
    }

    // ---------------------------------------------------------------------------------------
    // Display helpers
    // ---------------------------------------------------------------------------------------

    /**
     * Open the filed mandate document.
     */
    handleOpenDocument(): void {

        const documentId = this.state?.sepaMandateDocumentDtoId;

        if (!documentId) {
            return;
        }

        this.objectService.openObjectViaDialog(
            null,
            'dm.DmDocument',
            documentId,
            null,
            true,
            false,
            () => {
                //
            },
            MvsCrudModeEnum.read,
            'Object');
    }

    getStatus(): CustomerSepaMandateStatusEnum {
        return this.state?.sepaMandateStatus ?? CustomerSepaMandateStatusEnum.none;
    }

    /**
     * What the user is told about a mandate that is under way or has failed.
     *
     * {@code failed} is reported here only when the backend set no {@code errorMessage} — with one
     * the dedicated error box above already says it, and repeating it would be noise. Returns null
     * for {@code none} (nothing started yet), {@code active} (the summary speaks for itself) and
     * {@code revoked} (the summary carries the revocation date).
     */
    getProgressMessage(): CrSepaMandateProgressMessage | null {

        switch (this.getStatus()) {
            case CustomerSepaMandateStatusEnum.documentPending:
                return {
                    severity: 'info',
                    text: 'Das Mandat ist angelegt und wartet auf das unterschriebene Dokument.'
                };
            case CustomerSepaMandateStatusEnum.signaturePending:
                return {
                    severity: 'info',
                    text: 'Das Mandat wurde erzeugt und liegt im Unterschriftenprozess. '
                        + 'Es wird erst mit der Unterschrift gültig.'
                };
            case CustomerSepaMandateStatusEnum.failed:

                if (this.state?.errorFlag) {
                    return null;
                }

                return {
                    severity: 'error',
                    text: 'Die Mandatserfassung ist fehlgeschlagen. Es liegt kein gültiges Mandat vor.'
                };
            default:
                return null;
        }
    }

    /**
     * The capture area is offered while no valid mandate exists yet. An active mandate is shown as
     * a summary instead — it is changed by revoking it, not by overwriting it.
     */
    isCaptureVisible(): boolean {

        return !this.isReadonly
            && !!this.bankAccountId
            && this.getStatus() !== CustomerSepaMandateStatusEnum.active;
    }

    /**
     * The create context has no anchor before the account was saved: {@code withSmProcess} needs a
     * persisted account for the signing process and as a template variable.
     */
    isWaitingForBankAccount(): boolean {
        return !this.isReadonly && !this.bankAccountId;
    }

    getBranchInternalType(): CustomerSepaMandateSourceInternalTypeEnum {

        if (this.selectedSource) {
            return this.selectedSource.internalType;
        }

        return this.state?.sourceInternalType ?? null;
    }

    getStatusBadge(): CrSepaMandateBadge {

        switch (this.getStatus()) {
            case CustomerSepaMandateStatusEnum.documentPending:
                return {label: 'Dokument ausstehend', icon: 'fa-regular fa-file-arrow-up', severity: 'warn'};
            case CustomerSepaMandateStatusEnum.signaturePending:
                return {label: 'Unterschrift ausstehend', icon: 'fa-regular fa-signature', severity: 'warn'};
            case CustomerSepaMandateStatusEnum.active:
                return {label: 'Aktiv', icon: 'fa-regular fa-circle-check', severity: 'success'};
            case CustomerSepaMandateStatusEnum.revoked:
                return {label: 'Widerrufen', icon: 'fa-regular fa-ban', severity: 'danger'};
            case CustomerSepaMandateStatusEnum.failed:
                return {label: 'Fehlgeschlagen', icon: 'fa-regular fa-triangle-exclamation', severity: 'danger'};
            default:
                return {label: 'Kein Mandat', icon: 'fa-regular fa-circle-minus', severity: 'secondary'};
        }
    }

    getInternalTypeBadge(internalType: CustomerSepaMandateSourceInternalTypeEnum): CrSepaMandateBadge {

        switch (internalType) {
            case CustomerSepaMandateSourceInternalTypeEnum.withDocument:
                return {label: 'Mit Dokument', icon: 'fa-regular fa-file-lines', severity: 'info'};
            case CustomerSepaMandateSourceInternalTypeEnum.withSmProcess:
                return {label: 'Mit Unterschriftenprozess', icon: 'fa-regular fa-signature', severity: 'contrast'};
            // F10: co-signed on the host's application (cw „SEPA-Mandat im Antrag") - the wording of
            // getSepaMandateSourceCaptureTypeLabel, so both surfaces name the source the same way.
            case CustomerSepaMandateSourceInternalTypeEnum.withHostSignature:
                return {
                    label: 'Auf dem Antrag mitunterschrieben',
                    icon: 'fa-regular fa-file-signature',
                    severity: 'info'
                };
            case CustomerSepaMandateSourceInternalTypeEnum.withoutDocumentNoReason:
                return {
                    label: 'Ohne Dokument, ohne Begründung',
                    icon: 'fa-regular fa-file-circle-minus',
                    severity: 'secondary'
                };
            default:
                return {label: 'Ohne Dokument', icon: 'fa-regular fa-file-slash', severity: 'secondary'};
        }
    }

    getSignerStatusLabel(signerRole: CrSepaMandateSignerRoleDto): string {

        switch (signerRole?.status) {
            case CrSepaMandateSignerStatusEnum.readyForSign:
                return 'Bereit zur Unterschrift';
            case CrSepaMandateSignerStatusEnum.signed:
                return 'Unterschrieben';
            case CrSepaMandateSignerStatusEnum.sentForExternal:
                return 'Extern versendet';
            case CrSepaMandateSignerStatusEnum.externallySigned:
                return 'Extern unterschrieben';
            case CrSepaMandateSignerStatusEnum.declined:
                return 'Abgelehnt';
            case CrSepaMandateSignerStatusEnum.expired:
                return 'Abgelaufen';
            case CrSepaMandateSignerStatusEnum.skipped:
                return 'Übersprungen';
            case CrSepaMandateSignerStatusEnum.unassigned:
                return 'Nicht zugeordnet';
            default:
                return 'Ausstehend';
        }
    }

    getHeaderLabel(): string {
        return this.mode === 'create' ? 'SEPA-Mandat erfassen' : 'SEPA-Mandat';
    }

    getCaptureLabel(): string {
        return this.mode === 'create' ? 'Mandat erfassen' : 'Mandat nachträglich erfassen';
    }
}
